import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Handshake, LayoutGrid, Megaphone, Receipt, ShieldAlert, ShoppingCart, Trophy, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, C } from "../lib/api";
import { getAsset } from "../assets/assetManifest";
import { Overview } from "../components/admin/Overview";
import { TablesPanel } from "../components/admin/TablesPanel";
import { PlayersPanel } from "../components/admin/PlayersPanel";
import { AnnouncementsPanel, BroadcastPanel } from "../components/admin/AnnouncementsPanel";
import { ClubRoomsPanel } from "../components/admin/ClubRoomsPanel";
import { HousePlayersPanel } from "../components/admin/HousePlayersPanel";
import { MaintenancePanel } from "../components/admin/MaintenancePanel";
import { StorePanel } from "../components/admin/StorePanel";
import { AffiliatesPanel } from "../components/admin/AffiliatesPanel";
import { SalesPanel } from "../components/admin/SalesPanel";
import { MttPanel } from "../components/admin/MttPanel";
import { IntegrityPanel } from "../components/admin/IntegrityPanel";
import { PromoPanel } from "../components/admin/PromoPanel";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "tables", label: "Tables", icon: LayoutGrid },
  { id: "tournaments", label: "Tournaments", icon: Trophy },
  { id: "players", label: "Players", icon: Users },
  { id: "affiliates", label: "Affiliates", icon: Handshake },
  { id: "integrity", label: "Integrity", icon: ShieldAlert },
  { id: "ticker", label: "Ticker & notify", icon: Megaphone },
  { id: "store", label: "Store", icon: ShoppingCart },
  { id: "sales", label: "Sales", icon: Receipt }, // will sit behind a PIN once the room grows
];

// Staff dashboard (/admin): role admin | staff. Black/gold like the lobby, left-aligned, dense.
export default function AdminPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(() => localStorage.getItem("pfp_admin_tab") || "overview");
  const [newFlags, setNewFlags] = useState(0); // red count on the Integrity tab when the scan finds something new
  useEffect(() => { localStorage.setItem("pfp_admin_tab", tab); }, [tab]);
  useEffect(() => {
    if (!C.STAFF_ROLES.includes(user.role)) return;
    const poll = () => api.get("/api/admin/integrity").then((r) => setNewFlags(r.data.run?.new_count || 0)).catch(() => {});
    poll();
    const id = setInterval(poll, 120000);
    return () => clearInterval(id);
  }, [user.role]);
  if (!C.STAFF_ROLES.includes(user.role)) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4 text-slate-300" data-testid="admin-forbidden">
        <div className="text-lg font-display">Staff only</div>
        <button onClick={() => nav("/lobby")} className="btn-gold rounded-full px-5 py-2 text-xs font-display">Back to the lobby</button>
      </div>
    );
  }
  return (
    <div className="min-h-screen px-6 py-6 md:px-14 relative noise" data-testid="admin-page"
      style={{ backgroundImage: `url(${getAsset("bg_lobby_main").path})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed", backgroundColor: "#000" }}>
      <div className="absolute inset-0 bg-black/70 pointer-events-none" />
      <div className="relative">
        <header className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] gold-text">Players Room · staff</div>
              <h1 className="text-2xl md:text-3xl font-display">Dashboard</h1>
            </div>
          </div>
          <nav className="glass rounded-full p-1 flex gap-1" data-testid="admin-tabs">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-display tracking-wider transition-colors ${tab === id ? "bg-amber-500 text-slate-900" : "text-slate-300 hover:bg-white/10"}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
                {id === "integrity" && newFlags > 0 && (
                  <span data-testid="admin-tab-integrity-count"
                    className="rounded-full bg-red-500 text-white text-[10px] leading-none px-1.5 py-0.5">{newFlags}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="text-xs text-slate-400">{user.display_name} · <span className="uppercase text-amber-300">{user.role}</span></div>
        </header>
        {tab === "overview" && <Overview />}
        {tab === "tables" && <div className="flex flex-col gap-6"><HousePlayersPanel /><TablesPanel onGoTournaments={() => setTab("tournaments")} /></div>}
        {tab === "tournaments" && <MttPanel />}
        {tab === "players" && <PlayersPanel isAdmin={user.role === "admin"} />}
        {tab === "affiliates" && <div className="flex flex-col gap-6"><ClubRoomsPanel /><AffiliatesPanel /></div>}
        {tab === "integrity" && <IntegrityPanel />}
        {tab === "ticker" && <div className="flex flex-col gap-6"><MaintenancePanel /><AnnouncementsPanel /><BroadcastPanel /></div>}
        {tab === "store" && <div className="flex flex-col gap-6"><PromoPanel /><StorePanel /></div>}
        {tab === "sales" && <SalesPanel />}
      </div>
    </div>
  );
}
